import { CSSProperties, FC, ReactElement, useState } from 'react';
import { BackstageTheme } from '@backstage/theme';
import { IconButton, makeStyles, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import WarningIcon from '@material-ui/icons/WarningOutlined';
import { WbTooltip, WbTooltipProps } from '../WbTooltip';

/**
 * WbInfoTooltip Component
 *
 * An accessible tooltip component designed specifically for icons that are NOT wrapped in buttons.
 * This component automatically wraps icons in an IconButton to make them keyboard-accessible
 * and provides tooltip functionality on hover, focus, and click.
 *
 * ## When to use WbInfoTooltip:
 * - For informational icons that need tooltips but don't have interactive functionality
 * - When you need to display contextual help or additional information via an icon
 * - For status indicators with explanatory tooltips
 *
 * ## When NOT to use WbInfoTooltip:
 * - If your icon is already wrapped in a button with onClick functionality, use WbTooltip directly instead
 * - For interactive elements that perform actions (use regular buttons with WbTooltip)
 *
 * ## Features:
 * - Automatically wraps icons in an accessible IconButton
 * - Supports keyboard navigation (Tab to focus, ESC to close tooltip)
 * - Click to toggle tooltip open/closed
 * - Hover and focus support
 * - Multiple severity levels with themed colors (Info, Warning, Error, Default)
 * - Optional custom icons
 * - Optional link support via the `to` prop
 *
 * ## Accessibility:
 * - Keyboard navigable (Tab key)
 * - ESC key closes the tooltip
 * - Proper ARIA labels
 * - Focus visible states
 *
 * @example
 * // Basic usage with default severity
 * <WbInfoTooltip label="This is helpful information" />
 *
 * @example
 * // With Info severity (blue themed)
 * <WbInfoTooltip
 *   severity={Severity.Info}
 *   label="Additional context about this feature"
 * />
 *
 * @example
 * // With custom icon
 * <WbInfoTooltip
 *   severity={Severity.Warning}
 *   label="Warning: This action cannot be undone"
 *   icon={<CustomWarningIcon />}
 * />
 *
 * @example
 * // With link
 * <WbInfoTooltip
 *   label="Learn more about this feature"
 *   to="https://docs.example.com"
 * />
 */

export enum Severity {
  Default,
  Info,
  Warning,
  Error,
}

const useStyles = makeStyles(() => ({
  tooltip: ({
    textColor,
    backgroundColor,
  }: {
    textColor?: string;
    backgroundColor?: string;
  }) => ({
    color: textColor ?? '#fff',
    backgroundColor: backgroundColor ?? '#000',
  }),
  iconWrapper: {
    position: 'relative',
    // prevents weird margin added on bottom of the icon
    '& > svg': {
      display: 'block',
    },
  },
  // make the icon button absolutely positioned so that the touch target is 24px even if the actual icon in the layout is smaller
  iconButton: {
    minHeight: 24,
    minWidth: 24,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}));

interface Props extends Omit<WbTooltipProps, 'children'> {
  severity?: Severity;
  iconSize?: 'default' | 'inherit' | 'large' | 'medium' | 'small';
  icon?: JSX.Element;
  to?: string;
  iconWrapperClassName?: string;
}

interface GenericProps extends Omit<WbTooltipProps, 'children'> {
  icon: ReactElement;
  textColor?: string;
  backgroundColor?: string;
  iconColor?: string;
  to?: string;
  iconWrapperClassName?: string;
}

const GenericTooltip: FC<GenericProps> = ({
  title,
  icon,
  textColor,
  backgroundColor,
  iconColor,
  to,
  iconWrapperClassName,
  ...tooltipProps
}) => {
  const classes = useStyles({
    textColor,
    backgroundColor,
  });

  const [open, setOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(!open);
  };

  const style: CSSProperties | undefined = iconColor
    ? { color: iconColor }
    : undefined;

  const tooltipClasses =
    textColor && backgroundColor ? { tooltip: classes.tooltip } : undefined;

  return (
    <div
      className={clsx(classes.iconWrapper, iconWrapperClassName)}
      style={style}
    >
      <WbTooltip
        classes={tooltipClasses}
        title={title ?? ''}
        open={open}
        setOpen={setOpen}
        {...tooltipProps}
      >
        {!to ? (
          <IconButton
            className={classes.iconButton}
            aria-label={typeof title === 'string' ? title : ''}
            onClick={handleClick}
          />
        ) : (
          <IconButton
            className={classes.iconButton}
            href={to}
            aria-label={typeof title === 'string' ? title : ''}
          />
        )}
      </WbTooltip>
      {icon}
    </div>
  );
};

export const WbInfoTooltip: FC<Props> = ({
  severity = Severity.Default,
  title,
  iconSize = 'small',
  icon,
  to,
  iconWrapperClassName,
  ...tooltipProps
}) => {
  const theme = useTheme() as BackstageTheme;

  switch (severity) {
    case Severity.Info:
      return (
        <GenericTooltip
          title={title}
          icon={icon ?? <InfoIcon fontSize={iconSize} />}
          backgroundColor={theme.palette.info.light}
          textColor={theme.palette.info.contrastText}
          iconColor={theme.palette.info.main}
          to={to}
          iconWrapperClassName={iconWrapperClassName}
          {...tooltipProps}
        />
      );
    case Severity.Warning:
      return (
        <GenericTooltip
          title={title}
          icon={icon ?? <WarningIcon fontSize={iconSize} />}
          backgroundColor={theme.palette.warning.light}
          textColor={theme.palette.warning.contrastText}
          iconColor={theme.palette.warning.main}
          to={to}
          iconWrapperClassName={iconWrapperClassName}
          {...tooltipProps}
        />
      );
    case Severity.Error:
      return (
        <GenericTooltip
          title={title}
          icon={icon ?? <ErrorIcon fontSize={iconSize} />}
          backgroundColor={theme.palette.banner.error}
          textColor={theme.palette.banner.text}
          iconColor={theme.palette.error.main}
          to={to}
          iconWrapperClassName={iconWrapperClassName}
          {...tooltipProps}
        />
      );
    default:
      return (
        <GenericTooltip
          title={title}
          icon={icon ?? <InfoIcon fontSize={iconSize} />}
          to={to}
          iconWrapperClassName={iconWrapperClassName}
          {...tooltipProps}
        />
      );
  }
};
